import {
  Button,
  Grid,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { ManagementConfig, SecondsPreset } from "types/productManagementTypes";

import React from "react";
import { useTranslation } from "react-i18next";

export const SLEEP_DELAY_OPTIONS: { value: string; label: string }[] = [
  { value: "-1", label: "Never" },
  { value: "15", label: "15 seconds" },
  { value: "30", label: "30 seconds" },
  { value: "60", label: "1 minute" },
  { value: "300", label: "5 minutes" },
  { value: "600", label: "10 minutes" },
  { value: "1800", label: "30 minutes" },
];

export const SCREEN_OFF_DELAY_OPTIONS: { value: string; label: string }[] = [
  { value: "-1", label: "Never" },
  { value: "3", label: "3 seconds" },
  { value: "10", label: "10 seconds" },
  { value: "30", label: "30 seconds" },
  { value: "60", label: "1 minute" },
  { value: "300", label: "5 minutes" },
  { value: "600", label: "10 minutes" },
];

export const IDLE_DELAY_PRESETS: SecondsPreset[] = [
  { value: 60, labelKey: "oneMinute" },
  { value: 120, labelKey: "twoMinutes" },
  { value: 300, labelKey: "fiveMinutes" },
  { value: 600, labelKey: "tenMinutes" },
];

export const IDLE_SHUTDOWN_PRESETS: SecondsPreset[] = [
  { value: 300, labelKey: "fiveMinutes" },
  { value: 600, labelKey: "tenMinutes" },
  { value: 1800, labelKey: "thirtyMinutes" },
  { value: 3600, labelKey: "oneHour" },
];

interface SecondsInputProps {
  label: string;
  value: number;
  presets: SecondsPreset[];
  onChange: (next: number) => void;
  disabled?: boolean;
}

export const SecondsInput: React.FC<SecondsInputProps> = ({
  label,
  value,
  presets,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation();
  return (
    <Stack gap={4}>
      <NumberInput
        label={label}
        description={t("seconds")}
        min={0}
        value={value ?? 0}
        onChange={(v) => onChange(numOrUndefined(v) ?? 0)}
        disabled={disabled}
      />
      <Group gap="xs">
        {presets.map((p) => (
          <Button
            key={p.value}
            size="compact-xs"
            variant={value === p.value ? "filled" : "default"}
            onClick={() => onChange(p.value)}
            disabled={disabled}
          >
            {t(p.labelKey)}
          </Button>
        ))}
      </Group>
    </Stack>
  );
};

// Seed values mirror the headset's ManagementConfig defaults. Because an
// "apply" is a full replace on the device, an unset field is pushed verbatim —
// so a fresh config (e.g. a new default config) must start from real device
// defaults, not zeros, or it would push brightness 0 / no kiosk / blank
// tracking. sleep/screenOff use -1 ("leave unchanged") like the device.
export const emptyManagementConfig = (): ManagementConfig => ({
  doOsUpdateAfterBooted: false,
  osDownloadUrl: "",
  osDownloadPath: "",
  osVersion: "",
  appDownloadUrl: "",
  appDownloadPath: "",
  dontDownload: false,
  brightness: 180,
  playBoundary: false,
  trackingSettings: "101",
  usbDebugging: true,
  sleepDelayTime: -1,
  screenOffDelayTime: -1,
  wifiAlwaysOn: true,
  systemUpdateOn: false,
  setAppAsHome: true,
  ipd: 66.5,
  checktimeout: 15,
  idleDelayTime: 120,
  idleShutdownTime: 600,
  appAutoUpdate: true,
});

/** Keeps only the fields the config declares, dropping anything an older entry carries. */
const pickConfigFields = (fields: Record<string, unknown>): ManagementConfig => {
  const picked: Record<string, unknown> = {};
  for (const field of Object.keys(emptyManagementConfig())) {
    if (field in fields) picked[field] = fields[field];
  }
  return picked as unknown as ManagementConfig;
};

export const mergeStoredConfig = (stored?: Partial<ManagementConfig> | null): ManagementConfig => {
  const merged = pickConfigFields({ ...emptyManagementConfig(), ...(stored ?? {}) });
  if (merged.sleepDelayTime === 0) merged.sleepDelayTime = -1;
  if (merged.screenOffDelayTime === 0) merged.screenOffDelayTime = -1;
  return merged;
};

export interface ConfigSectionProps {
  value: ManagementConfig;
  onChange: (next: ManagementConfig) => void;
  disabled?: boolean;
}

const numOrUndefined = (v: string | number | undefined | null): number | undefined => {
  if (v === "" || v === null || v === undefined) return undefined;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const makeSet =
  (value: ManagementConfig, onChange: (next: ManagementConfig) => void) =>
  <K extends keyof ManagementConfig>(key: K, v: ManagementConfig[K]) =>
    onChange({ ...value, [key]: v });

interface SectionHeaderProps {
  title: string;
  hint?: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, hint }) => (
  <Stack gap={2}>
    <Title order={5}>{title}</Title>
    {hint && (
      <Text size="xs" c="dimmed">
        {hint}
      </Text>
    )}
  </Stack>
);

export const DisplaySection: React.FC<ConfigSectionProps> = ({ value, onChange, disabled }) => {
  const set = makeSet(value, onChange);
  return (
    <Stack gap="sm">
      <SectionHeader title="Display & Optics" />
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <NumberInput
            label="Brightness"
            description="0–255"
            min={0}
            max={255}
            value={value.brightness ?? 0}
            onChange={(v) => set("brightness", numOrUndefined(v) ?? 0)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <NumberInput
            label="IPD"
            description="Interpupillary distance — not applied by current headset firmware"
            min={0}
            value={value.ipd ?? 0}
            onChange={(v) => set("ipd", numOrUndefined(v) ?? 0)}
            disabled={disabled}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

const TRACKING_CHOICES: { value: string; label: string }[] = [
  { value: "0", label: "Off" },
  { value: "1", label: "On" },
];

const TRACKING_PARTS: { index: number; label: string }[] = [
  { index: 0, label: "Default safety zone" },
  { index: 1, label: "Boundary confirmation screen" },
  { index: 2, label: "6DOF tracking" },
];

const trackingCharAt = (settings: string | undefined, index: number): string =>
  settings?.[index] === "1" ? "1" : "0";

export const SystemSettingsSection: React.FC<ConfigSectionProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const set = makeSet(value, onChange);
  const setTrackingChar = (index: number, char: string) => {
    const chars = [0, 1, 2].map((i) =>
      i === index ? char : trackingCharAt(value.trackingSettings, i),
    );
    set("trackingSettings", chars.join(""));
  };
  return (
    <Stack gap="sm">
      <SectionHeader
        title="System Settings"
        hint="The headset writes this whole group in a single call — these settings are always applied together."
      />
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Switch
            label="Play boundary"
            checked={!!value.playBoundary}
            onChange={(e) => set("playBoundary", e.currentTarget.checked)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Switch
            label="USB debugging"
            checked={!!value.usbDebugging}
            onChange={(e) => set("usbDebugging", e.currentTarget.checked)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Switch
            label="Wi-Fi always on"
            description="Part of this group, not of the Wi-Fi connection"
            checked={!!value.wifiAlwaysOn}
            onChange={(e) => set("wifiAlwaysOn", e.currentTarget.checked)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Switch
            label="System update on"
            checked={!!value.systemUpdateOn}
            onChange={(e) => set("systemUpdateOn", e.currentTarget.checked)}
            disabled={disabled}
          />
        </Grid.Col>
        {TRACKING_PARTS.map((part) => (
          <Grid.Col key={part.index} span={{ base: 12, sm: 4 }}>
            <Select
              label={part.label}
              data={TRACKING_CHOICES}
              value={trackingCharAt(value.trackingSettings, part.index)}
              onChange={(v) => v && setTrackingChar(part.index, v)}
              allowDeselect={false}
              disabled={disabled}
            />
          </Grid.Col>
        ))}
      </Grid>
    </Stack>
  );
};

export const PowerTimeoutsSection: React.FC<ConfigSectionProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const set = makeSet(value, onChange);
  return (
    <Stack gap="sm">
      <SectionHeader title="Power & Timeouts" />
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select
            label="Sleep delay"
            data={SLEEP_DELAY_OPTIONS}
            value={String(value.sleepDelayTime ?? -1)}
            onChange={(v) => set("sleepDelayTime", numOrUndefined(v) ?? -1)}
            allowDeselect={false}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select
            label="Screen-off delay"
            data={SCREEN_OFF_DELAY_OPTIONS}
            value={String(value.screenOffDelayTime ?? -1)}
            onChange={(v) => set("screenOffDelayTime", numOrUndefined(v) ?? -1)}
            allowDeselect={false}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <SecondsInput
            label={t("idleDelay")}
            value={value.idleDelayTime ?? 0}
            presets={IDLE_DELAY_PRESETS}
            onChange={(v) => set("idleDelayTime", v)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <SecondsInput
            label={t("idleShutdown")}
            value={value.idleShutdownTime ?? 0}
            presets={IDLE_SHUTDOWN_PRESETS}
            onChange={(v) => set("idleShutdownTime", v)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <NumberInput
            label="Check timeout (s)"
            description="Connection ping period"
            min={0}
            value={value.checktimeout ?? 0}
            onChange={(v) => set("checktimeout", numOrUndefined(v) ?? 0)}
            disabled={disabled}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export const KioskSection: React.FC<ConfigSectionProps> = ({ value, onChange, disabled }) => {
  const set = makeSet(value, onChange);
  return (
    <Stack gap="sm">
      <SectionHeader title="Kiosk Mode" />
      <Switch
        label="Set app as home"
        description="Makes the app the device's home launcher"
        checked={!!value.setAppAsHome}
        onChange={(e) => set("setAppAsHome", e.currentTarget.checked)}
        disabled={disabled}
      />
      <Switch
        label="Install Auto releases by itself"
        checked={value.appAutoUpdate !== false}
        onChange={(e) => set("appAutoUpdate", e.currentTarget.checked)}
        disabled={disabled}
      />
    </Stack>
  );
};

export const UpdatesSection: React.FC<ConfigSectionProps> = ({ value, onChange, disabled }) => {
  const set = makeSet(value, onChange);
  return (
    <Stack gap="sm">
      <SectionHeader
        title="OS / App Update Sources"
        hint="Stored on the device and used only when an update runs (or at boot for 'Update OS after boot'). Pushing these fields does not start an update."
      />
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Switch
            label="Update OS after boot"
            checked={!!value.doOsUpdateAfterBooted}
            onChange={(e) => set("doOsUpdateAfterBooted", e.currentTarget.checked)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Switch
            label="Skip downloads"
            checked={!!value.dontDownload}
            onChange={(e) => set("dontDownload", e.currentTarget.checked)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="OS version"
            value={value.osVersion ?? ""}
            onChange={(e) => set("osVersion", e.currentTarget.value)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="OS download URL"
            value={value.osDownloadUrl ?? ""}
            onChange={(e) => set("osDownloadUrl", e.currentTarget.value)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="OS download path"
            value={value.osDownloadPath ?? ""}
            onChange={(e) => set("osDownloadPath", e.currentTarget.value)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="App download URL"
            value={value.appDownloadUrl ?? ""}
            onChange={(e) => set("appDownloadUrl", e.currentTarget.value)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="App download path"
            value={value.appDownloadPath ?? ""}
            onChange={(e) => set("appDownloadPath", e.currentTarget.value)}
            disabled={disabled}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

const ManagementConfigForm: React.FC<ConfigSectionProps> = (props) => (
  <Stack gap="lg">
    <DisplaySection {...props} />
    <SystemSettingsSection {...props} />
    <PowerTimeoutsSection {...props} />
    <KioskSection {...props} />
    <UpdatesSection {...props} />
  </Stack>
);

export default ManagementConfigForm;
